// src/App.js
import React from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CssBaseline from '@mui/material/CssBaseline';

// Import our custom theme provider
import { AppThemeProvider } from './contexts/ThemeContext';

// Import subscription notification system
import { SubscriptionNotificationProvider } from './contexts/SubscriptionNotificationContext';

// Import layouts
import AuthenticatedLayout from './layouts/AuthenticatedLayout';

// Import pages
import Dashboard from './pages/Dashboard';
import MarketingDashboard from './pages/MarketingDashboard';
import AutomationsList from './pages/Automations/List';
import ClientsList from './pages/Clients/List';
import ClientDetail from './pages/Clients/Detail'; // Import the new ClientDetail component
import ClientAlerts from './pages/Clients/Alerts';
import ClientAllergies from './pages/Clients/Allergies';
import ClientCommunications from './pages/Clients/Communications';
import ClientDocuments from './pages/Clients/Documents';
import ClientNotes from './pages/Clients/Notes';
import ClientPayments from './pages/Clients/Payments';
import ClientInvoices from './pages/Clients/Invoices';
import ClientForms from './pages/Clients/Forms';
import UsersList from './pages/Users/List';
import UserDetail from './pages/Users/Detail';
import CreateAutomation from './pages/Automations/Create';
import EditAutomation from './pages/Automations/Edit';
import ViewAutomation from './pages/Automations/Details';
import Settings from './pages/Settings';
import Login from './pages/Login';
import CalendarPage from './pages/Calendar';
import SchemaDebugPage from './pages/SchemaDebugPage';
import AudienceIndex from './pages/Audiences/index';
import AudienceCreate from './pages/Audiences/create';
import TestSchedules from './pages/Automations/TestSchedules';
import MetabaseDashboards from './pages/MetabaseDashboards';
import Signup from './pages/Signup';
import SignupSuccess from './pages/SignupSuccess';
import AuthCallback from './pages/AuthCallback';
import OnboardingWizard from './pages/Onboarding/OnboardingWizard';
// OnboardingSuccess removed - now handled within AccountSetupFlow
import OnboardingDevHelper from './pages/Onboarding/OnboardingDevHelper';
import Pricing from './pages/Pricing';
import CheckInbox from './pages/CheckInbox';
import Homepage from './pages/Homepage';
import TrialWarningLiveDemo, { TrialDemoProvider } from './components/TrialWarningLiveDemo';
import InvitationCompletion from './pages/InvitationCompletion';
import FormsManagementHub from './pages/Forms';
import FormCategoryView from './pages/Forms/CategoryView';
import FormTemplateView from './pages/Forms/TemplateView';
import FormDetail from './pages/Forms/FormDetail';
import ServiceCategoryView from './pages/Services/CategoryView';
import ServiceDetail from './pages/Services/ServiceDetail';
import ResetPassword from './pages/ResetPassword';
import ExpiredSubscription from './pages/ExpiredSubscription';
import SubscriptionSuccess from './pages/SubscriptionSuccess';

// Import new pages
import ServicesManagement from './pages/Services';
import FinancialDashboard from './pages/Financial';
import LeadsManagement from './pages/Leads';
import PipelineView from './pages/Leads/PipelineView';
import ReportsAnalytics from './pages/Reports';
import UpcomingFeature from './pages/UpcomingFeature';
import ActivityCenter from './pages/Activity';
import InvoicesManagement from './pages/Financial/Invoices';
import PaymentsManagement from './pages/Financial/Payments';
import QuotesManagement from './pages/Financial/Quotes';
import InvoiceDetail from './pages/Financial/InvoiceDetail';
import PaymentDetail from './pages/Financial/PaymentDetail';
import QuoteDetail from './pages/Financial/QuoteDetail';
import DiscountsManagement from './pages/Discounts';
import VouchersManagement from './pages/Vouchers';

// Import feature access control
import { hasFeatureAccess, getUpgradeMessage } from './utils/featureAccess';
import ProtectedPage from './components/ProtectedPage';

// Enhanced JWT Authentication System - Phase 1 Complete
import { AuthProvider, useAuth } from './contexts/AuthProvider';
import { AdminAuthProvider } from './contexts/AdminAuthProvider';
import { SessionWarning } from './components/auth';
import { AdminSessionWarning } from './components/admin';

// Admin portal imports
import AdminLogin from './pages/admin/AdminLogin';
import AdminAuthCallback from './pages/admin/AdminAuthCallback';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPasswordSetup from './pages/admin/AdminPasswordSetup';
import AdminPasswordReset from './pages/admin/AdminPasswordReset';
import AdminTenants from './pages/admin/AdminTenants';
import AdminTenantDetails from './pages/admin/AdminTenantDetails';
import AdminUsers from './pages/admin/AdminUsers';
import AdminActivity from './pages/admin/AdminActivity';
import AdminTenantProvisioning from './pages/admin/AdminTenantProvisioning';
import AdminMonitoring from './pages/admin/AdminMonitoring';
import AdminSyncMonitoring from './pages/admin/AdminSyncMonitoring';
import AdminTenantMetrics from './pages/admin/AdminTenantMetrics';
import AdminSync from './pages/admin/AdminSync';
import AdminImpersonation from './pages/admin/AdminImpersonation';

// Placeholder admin components - to be implemented
const AdminMigrations = () => (
  <div style={{padding: '20px'}}>
    <h1>Migration Tools</h1>
    <p>🚧 This page is under construction. Migration management features coming soon!</p>
    <p>This will include tools for managing authentication migrations from legacy to Stytch.</p>
  </div>
);

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // 1 minute
      retry: 1
    }
  }
});

// Auth guard component - integrated with enhanced AuthProvider
const AuthGuard = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading state while AuthProvider initializes
  if (isLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Homepage or Dashboard redirect based on auth
const RootRedirect = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading state while AuthProvider initializes
  if (isLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  // Redirect to dashboard if authenticated, otherwise show homepage
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Homepage />;
};

// Feature guard component - protects routes based on subscription tier
const FeatureGuard = ({ feature, children }) => {
  return (
    <ProtectedPage feature={feature}>
      {children}
    </ProtectedPage>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AdminAuthProvider>
          <AppThemeProvider>
            <SubscriptionNotificationProvider>
              <TrialDemoProvider>
              <CssBaseline />
              <BrowserRouter>
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/check-inbox" element={<CheckInbox />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup-success" element={<SignupSuccess />} />
            <Route path="/onboarding" element={<OnboardingWizard />} />
            <Route path="/onboarding-dev-helper" element={<OnboardingDevHelper />} />
            {/* OnboardingSuccess route removed - now handled within AccountSetupFlow */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/invitation/:token" element={<InvitationCompletion />} />
            <Route path="/expired-subscription" element={<ExpiredSubscription />} />
            <Route path="/subscription-success" element={<SubscriptionSuccess />} />


            {/* Admin Portal Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/auth/callback" element={<AdminAuthCallback />} />
            <Route path="/admin/auth/password-setup" element={<AdminPasswordSetup />} />
            <Route path="/admin/auth/reset-password" element={<AdminPasswordReset />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/tenants" element={<AdminTenants />} />
            <Route path="/admin/tenants/:tenantId" element={<AdminTenantDetails />} />
            <Route path="/admin/provision-tenant" element={<AdminTenantProvisioning />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/activity" element={<AdminActivity />} />
            <Route path="/admin/monitoring" element={<AdminMonitoring />} />
            <Route path="/admin/sync/monitoring" element={<AdminSyncMonitoring />} />
            <Route path="/admin/sync/monitoring/:tenantId" element={<AdminTenantMetrics />} />
            <Route path="/admin/sync" element={<AdminSync />} />
            <Route path="/admin/migrations" element={<AdminMigrations />} />
            <Route path="/admin/impersonation" element={<AdminImpersonation />} />
            
            {/* Protected routes */}
            <Route element={
              <AuthGuard>
                <AuthenticatedLayout />
              </AuthGuard>
            }>
              <Route path="dashboard" element={<Dashboard />} />
              
              {/* Marketing temporarily routed to Upcoming */}
              <Route path="marketing-dashboard" element={<UpcomingFeature />} />
              
              {/* Automations - Protected by tier */}
              <Route path="automations">
                <Route index element={
                  <FeatureGuard feature="automations">
                    <AutomationsList />
                  </FeatureGuard>
                } />
                <Route path="create" element={
                  <FeatureGuard feature="automations">
                    <CreateAutomation />
                  </FeatureGuard>
                } />
                <Route path=":id/edit" element={
                  <FeatureGuard feature="automations">
                    <EditAutomation />
                  </FeatureGuard>
                } />
                <Route path=":id" element={
                  <FeatureGuard feature="automations">
                    <ViewAutomation />
                  </FeatureGuard>
                } />
                <Route path="test-schedules" element={
                  <FeatureGuard feature="automations">
                    <TestSchedules />
                  </FeatureGuard>
                } />
              </Route>
              
              {/* Add audience routes here - Protected by tier */}
              <Route path="audiences">
                <Route index element={
                  <FeatureGuard feature="audiences">
                    <AudienceIndex />
                  </FeatureGuard>
                } />
                <Route path="create" element={
                  <FeatureGuard feature="audiences">
                    <AudienceCreate />
                  </FeatureGuard>
                } />
                {/* Add any other audience-related routes */}
              </Route>
              
              <Route path="clients">
                <Route index element={<ClientsList />} />
                <Route path=":id" element={<ClientDetail />} />
                <Route path=":id/alerts" element={<ClientAlerts />} />
                <Route path=":id/allergies" element={<ClientAllergies />} />
                <Route path=":id/communications" element={<ClientCommunications />} />
                <Route path=":id/documents" element={<ClientDocuments />} />
                <Route path=":id/notes" element={<ClientNotes />} />
                <Route path=":id/payments" element={<ClientPayments />} />
                <Route path=":id/invoices" element={<ClientInvoices />} />
                <Route path=":id/forms" element={<ClientForms />} />
              </Route>

              {/* Users & Staff - Protected by tier */}
              <Route path="users">
                <Route index element={
                  <FeatureGuard feature="users">
                    <UsersList />
                  </FeatureGuard>
                } />
                <Route path=":userId" element={
                  <FeatureGuard feature="users">
                    <UserDetail />
                  </FeatureGuard>
                } />
              </Route>

              <Route path="calendar" element={<CalendarPage />} />
              
              {/* Generic upcoming route for disabled sections */}
              <Route path="upcoming/:feature" element={<UpcomingFeature />} />

              {/* Forms Management */}
              <Route path="forms">
                <Route index element={<FormsManagementHub />} />
                <Route path="category/:categoryId" element={<FormCategoryView />} />
                <Route path="template/:formId" element={<FormTemplateView />} />
                <Route path="submission/:formId" element={<FormDetail />} />
                <Route path="create" element={<div>Form Builder - Coming Soon</div>} />
                <Route path="submissions" element={<div>All Form Submissions - Coming Soon</div>} />
              </Route>
              
              {/* Services & Products - Protected by tier */}
              <Route path="services">
                <Route index element={
                  <FeatureGuard feature="services">
                    <ServicesManagement />
                  </FeatureGuard>
                } />
                <Route path="category/:categoryId" element={
                  <FeatureGuard feature="services">
                    <ServiceCategoryView />
                  </FeatureGuard>
                } />
                <Route path="create" element={
                  <FeatureGuard feature="services">
                    <div>Service Builder - Coming Soon</div>
                  </FeatureGuard>
                } />
                <Route path="service/:serviceId" element={
                  <FeatureGuard feature="services">
                    <ServiceDetail />
                  </FeatureGuard>
                } />
              </Route>
              
              {/* Financial - Protected by tier */}
              <Route path="financial">
                <Route index element={
                  <FeatureGuard feature="financial">
                    <FinancialDashboard />
                  </FeatureGuard>
                } />
                <Route path="invoices" element={
                  <FeatureGuard feature="financial">
                    <InvoicesManagement />
                  </FeatureGuard>
                } />
                <Route path="invoices/:id" element={
                  <FeatureGuard feature="financial">
                    <InvoiceDetail />
                  </FeatureGuard>
                } />
                <Route path="payments" element={
                  <FeatureGuard feature="financial">
                    <PaymentsManagement />
                  </FeatureGuard>
                } />
                <Route path="payments/:id" element={
                  <FeatureGuard feature="financial">
                    <PaymentDetail />
                  </FeatureGuard>
                } />
                <Route path="quotes" element={
                  <FeatureGuard feature="financial">
                    <QuotesManagement />
                  </FeatureGuard>
                } />
                <Route path="quotes/:id" element={
                  <FeatureGuard feature="financial">
                    <QuoteDetail />
                  </FeatureGuard>
                } />
              </Route>
              
              {/* Individual financial routes - legacy paths */}
              <Route path="invoices" element={
                <FeatureGuard feature="financial">
                  <InvoicesManagement />
                </FeatureGuard>
              } />
              <Route path="payments" element={
                <FeatureGuard feature="financial">
                  <PaymentsManagement />
                </FeatureGuard>
              } />
              <Route path="quotes" element={
                <FeatureGuard feature="financial">
                  <QuotesManagement />
                </FeatureGuard>
              } />
              <Route path="discounts" element={
                <FeatureGuard feature="financial">
                  <DiscountsManagement />
                </FeatureGuard>
              } />
              <Route path="vouchers" element={
                <FeatureGuard feature="financial">
                  <VouchersManagement />
                </FeatureGuard>
              } />
              
              {/* Leads & Pipeline - Protected by tier */}
              <Route path="leads">
                <Route index element={
                  <FeatureGuard feature="leads">
                    <LeadsManagement />
                  </FeatureGuard>
                } />
                <Route path="pipeline" element={
                  <FeatureGuard feature="leads">
                    <PipelineView />
                  </FeatureGuard>
                } />
              </Route>
              
              {/* Reports */}
              <Route path="reports" element={<ReportsAnalytics />} />
              
              {/* Activity Center - Protected by tier */}
              <Route path="activity" element={
                <FeatureGuard feature="activity">
                  <ActivityCenter />
                </FeatureGuard>
              } />
              
              <Route path="settings" element={<Settings />} />
              <Route path="debug-schema" element={<SchemaDebugPage />} />
              <Route path="trial-demo" element={<TrialWarningLiveDemo />} />


              {/* Analytics - Protected by tier */}
              <Route path="metabase-dashboards" element={
                <FeatureGuard feature="analytics">
                  <MetabaseDashboards />
                </FeatureGuard>
              } />
            </Route>


            
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </BrowserRouter>
          <ToastContainer />
          {/* Session Warning Components - Global */}
          <SessionWarning />
          <AdminSessionWarning />
            </TrialDemoProvider>
          </SubscriptionNotificationProvider>
        </AppThemeProvider>
        </AdminAuthProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
